"use client";
import * as React from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import {
  Loader2,
  User as UserIcon,
  Lock,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Activity,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api-client";
import { writeSessionInfoClient } from "@/lib/session";

const LoginSchema = z.object({
  username: z.string().trim().min(1, "请输入工号或用户名"),
  password: z.string().min(1, "请输入密码"),
});
type LoginForm = z.infer<typeof LoginSchema>;

interface LoginResponse {
  session_token: string;
  username: string;
  role: string;
  expires_at: string;
  display_name?: string;
  employee_id?: string;
  must_change_password?: boolean;
}

export default function LoginPage() {
  return (
    <React.Suspense fallback={null}>
      <LoginInner />
    </React.Suspense>
  );
}

function LoginInner() {
  const router = useRouter();
  const search = useSearchParams();
  const [submitting, setSubmitting] = React.useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(LoginSchema) });

  const nextPath = search.get("next") || "";
  React.useEffect(() => {
    if (nextPath && nextPath !== "/") {
      toast.info("请重新登录后继续", { id: "session-expired" });
    }
  }, [nextPath]);

  const onSubmit = async (data: LoginForm) => {
    setSubmitting(true);
    try {
      const resp = await api.rpc<LoginResponse>("xyt_login", {
        p_username: data.username,
        p_password: data.password,
        p_session_hours: 12,
      });

      if (!resp?.session_token) {
        // 对齐 HTML 登录失败文案 @4590
        throw new ApiError("账号或密码错误，请联系管理员确认账号。", "BAD_CREDENTIALS", 401);
      }

      // R26：token cookie 由后端 xyt_login 返回的 Set-Cookie(HttpOnly) 写入，
      // 客户端 JS 不再自己写 —— 原来 document.cookie 设的 cookie 不可能是 HttpOnly，
      // XSS 就能偷。现在浏览器已经在 fetch 响应里拿到 HttpOnly cookie 并自动存了。
      writeSessionInfoClient({
        username: resp.username,
        role: resp.role,
        expiresAt: resp.expires_at,
        displayName: resp.display_name,
        employeeId: resp.employee_id,
        mustChangePassword: !!resp.must_change_password,
      });

      if (resp.must_change_password) {
        toast.warning("首次登录，请立即修改密码");
        router.push("/account?first=1");
      } else if (nextPath && /^\/[^/\\]/.test(nextPath) && !nextPath.startsWith("/login")) {
        // R27: next 参数必须是"/X"形式的内部路径，X 不能是 / 或 \。
        // 原来只检查 startsWith("/") 会放过 "//evil.com/x" —— 浏览器把 //host
        // 解析为 protocol-relative URL 跳到攻击者域（钓鱼站伪装登录成功后页面）。
        // 也挡掉 "/\evil.com"（旧 IE 同款协议相对路径兼容）。
        toast.success(`欢迎回来，${resp.display_name || resp.username}`);
        router.push(nextPath);
      } else {
        toast.success(`欢迎回来，${resp.display_name || resp.username}`);
        router.push("/dashboard");
      }
      router.refresh();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : (err as Error)?.message || "登录失败，请稍后重试";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative grid min-h-screen overflow-hidden lg:grid-cols-2">
      {/* ===== 背景装饰层（铺满全屏） ===== */}
      <AuthBackdrop />

      {/* ===== 左：品牌面板（桌面端显示） ===== */}
      <aside className="relative z-10 hidden flex-col justify-between p-12 lg:flex">
        <div
          className="flex items-center gap-3 animate-auth-fade-in"
          style={{ animationDelay: "50ms" }}
        >
          <LogoMark />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            XYT · Flow
          </span>
        </div>

        <div className="space-y-8">
          <div
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground animate-auth-fade-up"
            style={{ animationDelay: "150ms" }}
          >
            <span className="relative inline-flex size-2">
              <span className="absolute inset-0 rounded-full bg-emerald-500/40 animate-auth-pulse-dot" />
              <span className="relative inline-block size-2 rounded-full bg-emerald-500" />
            </span>
            System · Online
          </div>

          <h1
            className="text-5xl font-semibold leading-[1.05] tracking-tight animate-auth-fade-up"
            style={{ animationDelay: "250ms" }}
          >
            <span className="bg-gradient-to-br from-foreground via-foreground to-foreground/70 bg-clip-text text-transparent">
              迭代与研发
            </span>
            <br />
            <span className="bg-gradient-to-r from-indigo-500 via-violet-500 via-fuchsia-500 to-indigo-500 bg-clip-text text-transparent animate-auth-gradient-shift">
              全链路管理
            </span>
            <span
              aria-hidden
              className="ml-1 inline-block h-[0.9em] w-[3px] translate-y-[0.1em] bg-indigo-500 animate-auth-caret"
            />
          </h1>

          <p
            className="max-w-md text-sm leading-relaxed text-muted-foreground animate-auth-fade-up"
            style={{ animationDelay: "400ms" }}
          >
            一个系统覆盖项目立项、迭代审批、日常进度、研发流程、审计留痕 —— 专为 60
            人团队打造。
          </p>

          <ul className="space-y-3 text-sm">
            <Feature
              icon={ShieldCheck}
              label="4 级角色 + 10 项细粒度权限"
              delay="500ms"
            />
            <Feature
              icon={Activity}
              label="Realtime 同步 · 乐观锁 · 全量审计"
              delay="600ms"
            />
            <Feature
              icon={Sparkles}
              label="Linear / Vercel 级视觉体验"
              delay="700ms"
            />
          </ul>
        </div>

        <div
          className="flex items-center justify-between font-mono text-[11px] uppercase tracking-widest text-muted-foreground animate-auth-fade-in"
          style={{ animationDelay: "900ms" }}
        >
          <span>v1.0 · build {BUILD_SHORT}</span>
          <span>© {new Date().getFullYear()} XYT</span>
        </div>
      </aside>

      {/* ===== 右：登录卡片 ===== */}
      <main className="relative z-10 flex items-center justify-center p-6 sm:p-10">
        <div
          className="w-full max-w-md animate-auth-scale-in"
          style={{ animationDelay: "200ms" }}
        >
          {/* 移动端 LOGO（桌面隐藏） */}
          <div className="mb-8 flex items-center justify-center gap-3 lg:hidden">
            <LogoMark />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              XYT · Flow
            </span>
          </div>

          <GlassCard>
            {/* 顶部渐变线（科技感） */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-indigo-500/70 to-transparent" />

            {/* 一次性高光扫过 */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 -left-1 w-1/3 bg-gradient-to-r from-transparent via-white/15 to-transparent dark:via-white/10 animate-auth-card-shine-once"
            />

            <div
              className="space-y-2 pb-6 text-center animate-auth-fade-up"
              style={{ animationDelay: "350ms" }}
            >
              <h2 className="text-2xl font-semibold tracking-tight">登录账户</h2>
              <p className="text-sm text-muted-foreground">
                使用工号或用户名 + 密码登录
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div
                className="animate-auth-fade-up"
                style={{ animationDelay: "450ms" }}
              >
                <Field
                  label="工号 / 用户名"
                  icon={UserIcon}
                  error={errors.username?.message}
                >
                  <Input
                    id="username"
                    autoComplete="username"
                    autoFocus
                    disabled={submitting}
                    placeholder="e.g. zhang_san"
                    className="peer h-11 pl-10"
                    {...register("username")}
                  />
                </Field>
              </div>

              <div
                className="animate-auth-fade-up"
                style={{ animationDelay: "550ms" }}
              >
                <Field label="密码" icon={Lock} error={errors.password?.message}>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    disabled={submitting}
                    placeholder="••••••••"
                    className="peer h-11 pl-10"
                    {...register("password")}
                  />
                </Field>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                style={{ animationDelay: "650ms" }}
                className="group relative h-11 w-full overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-600 text-white hover:from-indigo-500 hover:via-indigo-400 hover:to-violet-500 animate-auth-fade-up"
              >
                {/* 按钮内部的 shine 高光（hover 时扫过） */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 -left-1 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-auth-shine"
                />
                <span className="relative flex items-center justify-center gap-2">
                  {submitting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      登录中…
                    </>
                  ) : (
                    <>
                      登录
                      <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                  )}
                </span>
              </Button>

              <p
                className="pt-1 text-center text-xs text-muted-foreground animate-auth-fade-in"
                style={{ animationDelay: "800ms" }}
              >
                首次登录请联系管理员获取初始密码
              </p>
            </form>
          </GlassCard>

          {/* 移动端 footer */}
          <div className="mt-6 flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground lg:hidden">
            <span className="size-1.5 rounded-full bg-emerald-500" />
            System · Online · v1.0
          </div>
        </div>
      </main>
    </div>
  );
}

/* ============ 子组件 ============ */

const BUILD_SHORT =
  (process.env.NEXT_PUBLIC_BUILD_SHA || "dev").slice(0, 7);

function LogoMark() {
  return (
    <span className="relative inline-flex size-9 items-center justify-center">
      {/* 静止的内容层（对角线 + 圆点） */}
      <svg viewBox="0 0 40 40" className="absolute inset-0 size-9" fill="none" aria-hidden>
        <defs>
          <linearGradient id="xyt-logo-grad" x1="0" y1="0" x2="40" y2="40">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#d946ef" />
          </linearGradient>
        </defs>
        <path
          d="M13 13 L27 27 M27 13 L20 20"
          stroke="url(#xyt-logo-grad)"
          strokeWidth="2.2"
          strokeLinecap="round"
        />
        <circle cx="20" cy="20" r="1.6" fill="url(#xyt-logo-grad)">
          <animate
            attributeName="r"
            values="1.6;2.2;1.6"
            dur="2.4s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>

      {/* 外框缓慢旋转层 */}
      <svg
        viewBox="0 0 40 40"
        className="absolute inset-0 size-9 animate-auth-logo-spin"
        fill="none"
        aria-hidden
      >
        <rect
          x="4"
          y="4"
          width="32"
          height="32"
          rx="8"
          stroke="url(#xyt-logo-grad)"
          strokeWidth="1.5"
          className="opacity-60"
          strokeDasharray="40 20"
        />
      </svg>
    </span>
  );
}

function Feature({
  icon: Icon,
  label,
  delay,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  delay?: string;
}) {
  return (
    <li
      className="group flex cursor-default items-center gap-3 animate-auth-slide-in-left transition-transform duration-300 hover:translate-x-1"
      style={{ animationDelay: delay }}
    >
      <span className="flex size-8 items-center justify-center rounded-md border border-border/60 bg-card/60 backdrop-blur-sm transition-all duration-300 group-hover:border-indigo-500/60 group-hover:shadow-[0_0_0_4px_hsl(234_89%_70%/0.12)]">
        <Icon className="size-4 text-indigo-500 transition-transform duration-300 group-hover:scale-110 dark:text-indigo-400" />
      </span>
      <span className="text-foreground/90 transition-colors duration-300 group-hover:text-foreground">
        {label}
      </span>
    </li>
  );
}

function Field({
  label,
  icon: Icon,
  error,
  children,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      {/*
        has([*:focus]) 让父容器在任意后代 input 聚焦时命中，
        驱动图标颜色平滑过渡为 indigo。
      */}
      <div className="relative [&:has(input:focus)>svg]:text-indigo-500 dark:[&:has(input:focus)>svg]:text-indigo-400">
        <Icon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground transition-colors duration-200" />
        {children}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function GlassCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border/70 bg-card/70 p-8 shadow-[var(--shadow-card)] backdrop-blur-xl">
      {children}
    </div>
  );
}

/* ============ 背景：网格 + 双光球 + 粒子网络 ============ */
function AuthBackdrop() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
      {/* 网格层 —— 用 CSS 画线 + 径向蒙版淡出 */}
      <div
        className="absolute inset-0 animate-auth-grid-drift opacity-[0.35] dark:opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(to right, hsl(var(--border)) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border)) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          maskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />

      {/* 左上光球 */}
      <div
        className="absolute -left-[10%] -top-[20%] size-[55vw] rounded-full animate-auth-float-a"
        style={{
          background:
            "radial-gradient(closest-side, hsl(234 89% 66% / 0.28), hsl(234 89% 66% / 0.08) 40%, transparent 72%)",
          filter: "blur(40px)",
        }}
      />

      {/* 右下光球 */}
      <div
        className="absolute -right-[10%] -bottom-[25%] size-[60vw] rounded-full animate-auth-float-b"
        style={{
          background:
            "radial-gradient(closest-side, hsl(292 84% 62% / 0.22), hsl(292 84% 62% / 0.06) 40%, transparent 72%)",
          filter: "blur(48px)",
        }}
      />

      {/* 中间浅青色光球（科技感） */}
      <div
        className="absolute left-1/3 top-1/2 size-[30vw] -translate-y-1/2 rounded-full animate-auth-float-a"
        style={{
          background:
            "radial-gradient(closest-side, hsl(190 95% 55% / 0.14), transparent 70%)",
          filter: "blur(60px)",
          animationDelay: "-7s",
        }}
      />

      {/* 粒子网络层（canvas） */}
      <ParticleField />

      {/* 顶部 & 底部渐隐遮罩（让网格边缘更柔） */}
      <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-background to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
}

/**
 * 星座粒子网络 —— 轻量 Canvas 实现：
 *  - 自适应屏幕密度 (~ 每 22000 像素一个粒子，上限 80)
 *  - 距离 < 130px 的两点连线，透明度随距离衰减
 *  - 鼠标附近的粒子轻微吸引 + 连线变亮（互动感）
 *  - DPR 感知、尊重 prefers-reduced-motion
 */
function ParticleField() {
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    // 暗色模式判断（跟 next-themes 的 .dark class 走）
    const isDark = () => document.documentElement.classList.contains("dark");

    type Particle = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      r: number;
    };
    let particles: Particle[] = [];
    let width = 0;
    let height = 0;
    let dpr = Math.max(1, window.devicePixelRatio || 1);

    const mouse = { x: -9999, y: -9999, active: false };

    const LINK_DIST = 130;
    const MOUSE_PULL = 0.02;

    function resize() {
      if (!canvas) return;
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.max(1, window.devicePixelRatio || 1);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      // 按屏幕大小计算粒子数
      const target = Math.min(
        80,
        Math.max(28, Math.floor((width * height) / 22000))
      );
      if (particles.length < target) {
        while (particles.length < target) particles.push(spawn());
      } else if (particles.length > target) {
        particles = particles.slice(0, target);
      }
    }

    function spawn(): Particle {
      return {
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.28,
        r: Math.random() * 1.4 + 0.6,
      };
    }

    function step() {
      ctx!.clearRect(0, 0, width, height);

      const dark = isDark();
      // 粒子点色：indigo-400 / indigo-500
      const dotColor = dark ? "rgba(165,180,252,0.85)" : "rgba(99,102,241,0.7)";
      const lineBase = dark ? "165,180,252" : "99,102,241";
      const lineAlpha = dark ? 0.35 : 0.28;
      const mouseAlpha = dark ? 0.55 : 0.45;

      // 更新位置
      for (const p of particles) {
        if (!reduceMotion) {
          p.x += p.vx;
          p.y += p.vy;

          // 鼠标吸引（轻微）
          if (mouse.active) {
            const dx = mouse.x - p.x;
            const dy = mouse.y - p.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < 180 * 180) {
              const d = Math.sqrt(d2) || 1;
              p.vx += (dx / d) * MOUSE_PULL * 0.15;
              p.vy += (dy / d) * MOUSE_PULL * 0.15;
            }
          }

          // 边界反弹（带缓冲）
          if (p.x < -20) p.x = width + 20;
          if (p.x > width + 20) p.x = -20;
          if (p.y < -20) p.y = height + 20;
          if (p.y > height + 20) p.y = -20;

          // 阻尼（防止被鼠标拖得越来越快）
          p.vx *= 0.995;
          p.vy *= 0.995;
          const speed = Math.hypot(p.vx, p.vy);
          if (speed > 0.6) {
            p.vx = (p.vx / speed) * 0.6;
            p.vy = (p.vy / speed) * 0.6;
          }
        }
      }

      // 连线
      ctx!.lineWidth = 1;
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < LINK_DIST * LINK_DIST) {
            const d = Math.sqrt(d2);
            const t = 1 - d / LINK_DIST;
            ctx!.strokeStyle = `rgba(${lineBase},${lineAlpha * t})`;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(b.x, b.y);
            ctx!.stroke();
          }
        }

        // 鼠标到粒子的连线（更亮）
        if (mouse.active) {
          const dx = a.x - mouse.x;
          const dy = a.y - mouse.y;
          const d2 = dx * dx + dy * dy;
          const R = 160;
          if (d2 < R * R) {
            const d = Math.sqrt(d2);
            const t = 1 - d / R;
            ctx!.strokeStyle = `rgba(${lineBase},${mouseAlpha * t})`;
            ctx!.beginPath();
            ctx!.moveTo(a.x, a.y);
            ctx!.lineTo(mouse.x, mouse.y);
            ctx!.stroke();
          }
        }
      }

      // 画粒子
      ctx!.fillStyle = dotColor;
      for (const p of particles) {
        ctx!.beginPath();
        ctx!.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx!.fill();
      }

      raf = requestAnimationFrame(step);
    }

    // 鼠标监听在 window 上（canvas 是 pointer-events:none）
    function onMouseMove(e: MouseEvent) {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
    }
    function onMouseLeave() {
      mouse.active = false;
      mouse.x = -9999;
      mouse.y = -9999;
    }

    resize();
    let raf = requestAnimationFrame(step);

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseout", onMouseLeave);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseout", onMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 size-full opacity-70 dark:opacity-90"
      style={{
        maskImage:
          "radial-gradient(ellipse at center, black 40%, transparent 85%)",
        WebkitMaskImage:
          "radial-gradient(ellipse at center, black 40%, transparent 85%)",
      }}
    />
  );
}
